import { downscalePhoto } from "~/form/downscale_photo";
import type { Provider, TimeSlot, ConfirmationData, FormData } from "./types";

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute("content") ?? "";
}

function getBasePath(): string {
  return window.location.pathname.replace(/\/$/, "");
}

function buildUrl(base: string, params: Record<string, string>): string {
  const url = new URL(base, window.location.origin);
  for (const [key, value] of Object.entries(params)) {
    if (value) url.searchParams.set(key, value);
  }
  return url.toString();
}

export type AvailabilityResponse = {
  providers: Provider[];
};

export async function fetchAvailability(
  _locationId: string,
  appointmentTemplateId: string,
  scheduleType: string,
): Promise<AvailabilityResponse> {
  const url = buildUrl(`${getBasePath()}/availability`, {
    appointment_template_id: appointmentTemplateId,
    schedule_type: scheduleType,
  });

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
  });

  if (!res.ok) throw new Error("Failed to fetch availability");

  const data = (await res.json()) as {
    providers: { id: string; name: string }[];
  };
  return {
    providers: data.providers.map((p) => ({
      id: p.id,
      name: p.name,
    })),
  };
}

export type WeekSlotsResponse = {
  /** Slots grouped by ISO date string (YYYY-MM-DD) */
  slotsByDate: Record<string, TimeSlot[]>;
  /** Included when the entire week has no slots */
  nextAvailableDate: string | null;
};

export async function fetchWeekSlots(
  _locationId: string,
  appointmentTemplateId: string,
  scheduleType: string,
  date: string,
  providerId: string | null,
  holdId: string | null,
): Promise<WeekSlotsResponse> {
  const params: Record<string, string> = {
    appointment_template_id: appointmentTemplateId,
    schedule_type: scheduleType,
    date,
  };
  if (providerId) params["provider_id"] = providerId;
  // Our own hold must keep showing as available, as it does at submit.
  if (holdId) params["hold_id"] = holdId;

  const url = buildUrl(`${getBasePath()}/slots`, params);

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
  });

  if (!res.ok) throw new Error("Failed to fetch slots");

  type RawSlot = { key: string; label: string; start_time: string; provider_name: string | null };
  const data = (await res.json()) as {
    slots_by_date: Record<string, RawSlot[]>;
    next_available_date?: string | null;
  };

  const slotsByDate: Record<string, TimeSlot[]> = {};
  for (const [dateKey, rawSlots] of Object.entries(data.slots_by_date)) {
    slotsByDate[dateKey] = rawSlots.map((s) => ({
      key: s.key,
      label: s.label,
      startTime: s.start_time,
      providerName: s.provider_name,
    }));
  }

  return {
    slotsByDate,
    nextAvailableDate: data.next_available_date ?? null,
  };
}

/**
 * "taken" is the server refusing the slot — the whole point of holding.
 * "unavailable" is us failing to ask: booking must carry on without a
 * reservation rather than stall, which is how this step behaved before holds
 * existed. Never collapse the two.
 */
export type HoldResult =
  | { status: "held"; holdId: string; expiresAt: string }
  | { status: "taken" }
  | { status: "unavailable" };

/** How long a patient waits on a reservation before we let them past without one. */
const HOLD_TIMEOUT_MS = 5000;

/** Reserves the slot for checkout. Pass the hold we already own so re-picking
 * the same time renews it instead of colliding with itself. */
export async function placeHold(
  _locationId: string,
  appointmentTemplateId: string,
  scheduleType: string,
  slotKey: string,
  holdId: string | null,
): Promise<HoldResult> {
  try {
    const res = await fetch(`${getBasePath()}/hold`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify({
        appointment_template_id: appointmentTemplateId,
        schedule_type: scheduleType,
        slot_key: slotKey,
        hold_id: holdId,
      }),
      signal: AbortSignal.timeout(HOLD_TIMEOUT_MS),
    });

    if (res.status === 422) return { status: "taken" };
    if (!res.ok) return { status: "unavailable" };

    const data = (await res.json()) as {
      held?: boolean;
      hold_id?: string;
      expires_at?: string;
    };

    if (data.held && data.hold_id && data.expires_at) {
      return { status: "held", holdId: data.hold_id, expiresAt: data.expires_at };
    }

    return { status: "unavailable" };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * One definition of the `schedule` payload. Both the deposit charge and the
 * booking rebuild the same ScheduleForm server-side, so they have to send the
 * same fields — a deposit taken against a different shape than the one booked
 * is money on the wrong slot.
 */
function scheduleParams(formData: FormData): Record<string, unknown> {
  return {
    patient_type: formData.patientType,
    appointment_template_public_id: formData.appointmentTemplateId,
    chief_complaint: formData.chiefComplaint,
    provider_public_id: formData.providerId,
    appointment_date: formData.appointmentDate,
    appointment_slot_key: formData.appointmentSlotKey,
    waitlist: formData.waitlist,
    first_name: formData.firstName,
    last_name: formData.lastName,
    preferred_name: formData.preferredName,
    dob: formData.dob,
    phone: formData.phone,
    email: formData.email,
    referred_from_selection: formData.referredFromSelection,
    referred_from_detail: formData.referredFromDetail,
    has_insurance: formData.hasInsurance,
    insurance_changed: formData.insuranceChanged,
    insurance_name: formData.insuranceName,
    insurance_subscriber_name: formData.insuranceSubscriberName,
    insurance_subscriber_dob: formData.insuranceSubscriberDob,
    insurance_subscriber_id: formData.insuranceSubscriberId,
    insurance_group_number: formData.insuranceGroupNumber,
    insurance_card_front_signed_id: formData.insuranceCardFront?.signedId ?? null,
    insurance_card_back_signed_id: formData.insuranceCardBack?.signedId ?? null,
    phone_verification_code: formData.phoneVerificationCode,
    phone_verification_id: formData.phoneVerificationId,
    marketing_click_id: formData.marketingClickId,
    hold_public_id: formData.holdId,
    language_picked: formData.languagePicked,
  };
}

export type DepositIntentResult =
  | {
      status: "ready";
      clientSecret: string;
      baseAmount: string;
      surchargeAmount: string | null;
      totalAmount: string;
    }
  | { status: "refused"; errors: string[] };

/**
 * Asks the server to open a deposit charge. The amount is NOT sent — the server
 * reads it off the slot reservation, so nothing the browser says can change
 * what is charged.
 */
export async function createDepositIntent(
  _locationId: string,
  formData: FormData,
  paymentMethodId: string,
  locale: string,
): Promise<DepositIntentResult> {
  const res = await fetch(`${getBasePath()}/deposit_intent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      payment_method_id: paymentMethodId,
      // The top-level locale is the language the server answers in; the form's records it on a
      // patient the deposit creates, as on submit.
      locale,
      schedule: { ...scheduleParams(formData), locale },
    }),
  });

  const data = (await res.json().catch(() => ({}))) as {
    secret?: string;
    base_amount?: string;
    surcharge_amount?: string | null;
    total_amount?: string;
    errors?: string[];
  };

  if (res.ok && data.secret && data.base_amount && data.total_amount) {
    return {
      status: "ready",
      clientSecret: data.secret,
      baseAmount: data.base_amount,
      surchargeAmount: data.surcharge_amount ?? null,
      totalAmount: data.total_amount,
    };
  }

  return { status: "refused", errors: data.errors ?? [] };
}

export type SubmitResult =
  | { success: true; confirmation: ConfirmationData }
  | { success: false; errors: string[] };

export async function submitSchedule(
  _locationId: string,
  formData: FormData,
  locale: string,
): Promise<SubmitResult> {
  const res = await fetch(getBasePath(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    // The page's locale rides along: the server answers in it and records it on the patient.
    body: JSON.stringify({ locale, schedule: { ...scheduleParams(formData), locale } }),
  });

  const data = (await res.json()) as {
    success?: boolean;
    confirmation?: {
      provider_name: string;
      start_time: string;
      end_time: string;
      appointment_id: string;
    };
    errors?: string[];
  };

  if (res.ok && data.success && data.confirmation) {
    return {
      success: true,
      confirmation: {
        providerName: data.confirmation.provider_name,
        startTime: data.confirmation.start_time,
        endTime: data.confirmation.end_time,
        appointmentId: data.confirmation.appointment_id,
      },
    };
  }

  return { success: false, errors: data.errors ?? ["An error occurred"] };
}

export async function sendVerificationCode(
  _locationId: string,
  phone: string,
  verificationId: string,
  locale: string,
): Promise<boolean> {
  const res = await fetch(`${getBasePath()}/send_verification`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    // The code is texted in the page's language.
    body: JSON.stringify({ phone, verification_id: verificationId, locale }),
  });

  return res.ok;
}

export async function verifyPhone(
  _locationId: string,
  phone: string,
  verificationId: string,
  code: string,
): Promise<boolean> {
  const res = await fetch(`${getBasePath()}/verify_phone`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ phone, verification_id: verificationId, code }),
  });

  return res.ok;
}

type TrackingTestTag = { service: string; pixelId: string; loaded: boolean };

/** Which of the test conversion's sinks had a loaded tag to receive it. */
export type TestConversionSinks = { gtm: boolean; meta: boolean; embedParent: boolean };

/** Tracking test mode: which of the location's tags loaded on this visit. Best effort. */
export async function reportTrackingTestVisit(
  code: string,
  tags: TrackingTestTag[],
): Promise<void> {
  try {
    await fetch(`${getBasePath()}/tracking_test/visit`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify({
        code,
        tags: tags.map((tag) => ({
          service: tag.service,
          pixel_id: tag.pixelId,
          loaded: tag.loaded,
        })),
      }),
    });
  } catch {
    /* the tracking test page just shows no visit report */
  }
}

/** Tracking test mode: which sinks the test conversion reached. */
export async function reportTestConversion(
  code: string,
  sinks: TestConversionSinks,
): Promise<boolean> {
  const res = await fetch(`${getBasePath()}/tracking_test/conversion`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      code,
      sinks: { gtm: sinks.gtm, meta: sinks.meta, embed_parent: sinks.embedParent },
    }),
  });

  return res.ok;
}

// Remembers a language picked from the menu for the patient's other pages, since the page switches
// in place without a visit. Best effort: the pick still holds on this page if it fails.
export async function rememberLanguagePick(locale: string): Promise<void> {
  try {
    await fetch(`${getBasePath()}/language`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify({ locale, language_pick: "1" }),
    });
  } catch {
    /* offline or blocked: nothing to undo */
  }
}

export type InsuranceCardUploadResult =
  | { status: "uploaded"; signedId: string }
  | { status: "failed" };

/**
 * Stores a card photo the moment it is picked, shrunk on the device first. The booking later names
 * it by signed id, so a failure here costs the photo and nothing else.
 */
export async function uploadInsuranceCard(file: File): Promise<InsuranceCardUploadResult> {
  try {
    const body = new FormData();
    body.append("file", await downscalePhoto(file));
    const res = await fetch(`${getBasePath()}/insurance_card`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body,
    });
    const data = (await res.json().catch(() => ({}))) as { signed_id?: string };
    if (res.ok && data.signed_id) return { status: "uploaded", signedId: data.signed_id };
    return { status: "failed" };
  } catch {
    return { status: "failed" };
  }
}
